<template>
  <div class="working-groups-page">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h4 class="card-title mb-0">
              <i class="bi bi-people me-2"></i>Working Groups
            </h4>
            <div class="d-flex gap-2">
              <button class="btn btn-outline-primary" :class="{ active: activeView === 'list' }" @click="activeView = 'list'">
                <i class="bi bi-list me-2"></i>List View
              </button>
              <button class="btn btn-outline-primary" :class="{ active: activeView === 'grid' }" @click="activeView = 'grid'">
                <i class="bi bi-grid me-2"></i>Grid View
              </button>
              <button class="btn btn-primary" @click="showCreateModal = true">
                <i class="bi bi-plus me-2"></i>Create Group
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading working groups...</p>
            </div>
            <!-- Filter and Search -->
            <div v-else class="row mb-4">
              <div class="col-md-6">
                <div class="d-flex gap-2">
                  <input type="text" class="form-control" placeholder="Search groups..." v-model="searchTerm">
                  <select class="form-select" v-model="filterStatus" style="width: 200px;">
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <!-- Grid View -->
            <div v-if="!loading && activeView === 'grid'" class="row">
              <div v-for="group in filteredGroups" :key="group.id" class="col-md-6 col-lg-4 mb-4">
                <div class="card h-100 group-card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                      <span class="badge" :class="getStatusBadgeClass(group.status)">
                        {{ group.status ? group.status.toUpperCase() : 'ACTIVE' }}
                      </span>
                      <div class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                          <i class="bi bi-three-dots"></i>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end">
                          <li><a class="dropdown-item" href="#" @click="viewGroup(group)">View Details</a></li>
                          <li><a class="dropdown-item" href="#" @click="editGroup(group)" v-if="group.status === 'active' && canManageGroup(group)">Edit</a></li>
                          <li><a class="dropdown-item" href="#" @click="manageMembers(group)" v-if="group.status === 'active' && canManageGroup(group)">Manage Members</a></li>
                          <li><hr class="dropdown-divider"></li>
                          <li v-if="group.status === 'active' && canManageGroup(group)">
                            <a class="dropdown-item text-danger" href="#" @click="showDeactivateConfirmation(group)">Deactivate</a>
                          </li>
                          <li v-if="group.status === 'inactive' && isSuperAdmin">
                            <a class="dropdown-item text-success" href="#" @click="showActivateConfirmation(group)">Activate</a>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <h5 class="card-title">{{ group.name }}</h5>
                    <div class="mb-3">
                      <div class="d-flex align-items-center mb-2" v-if="group.admins && group.admins.length > 0">
                        <i class="bi bi-person-badge me-2 text-primary"></i>
                        <span>
                          <strong>Admins:</strong>
                          <span v-for="(admin, index) in group.admins" :key="admin.id">
                            {{ admin.name }}<span v-if="index < group.admins.length - 1">, </span>
                          </span>
                        </span>
                      </div>
                      <div class="d-flex align-items-center">
                        <i class="bi bi-people me-2 text-success"></i>
                        <span>{{ group.member_count || 0 }} members</span>
                      </div>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <small class="text-muted">
                        Created {{ formatDate(group.created_at) }}
                      </small>
                      <div class="d-flex gap-1">
                        <button class="btn btn-sm btn-outline-primary" @click="viewGroup(group)">
                          <i class="bi bi-eye"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- List View -->
            <div v-if="!loading && activeView === 'list'" class="table-responsive">
              <table class="table table-hover">
                <thead>
                <tr>
                  <th>Group Name</th>
                  <th>Admin</th>
                  <th>Members</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="group in filteredGroups" :key="group.id">
                  <td>
                    <div>
                      <div class="fw-semibold">{{ group.name }}</div>
                    </div>
                  </td>
                  <td>
                    <div v-if="group.admins && group.admins.length > 0">
                      <span v-for="(admin, index) in group.admins" :key="admin.id">
                        {{ admin.name }}<span v-if="index < group.admins.length - 1">, </span>
                      </span>
                    </div>
                    <span v-else class="text-muted">No admins assigned</span>
                  </td>
                  <td>
                    <span class="badge bg-primary">{{ group.member_count || 0 }}</span>
                  </td>
                  <td>
                      <span class="badge" :class="getStatusBadgeClass(group.status)">
                        {{ group.status || 'active' }}
                      </span>
                  </td>
                  <td>{{ formatDate(group.created_at) }}</td>
                  <td>
                    <div class="dropdown">
                      <button class="btn btn-sm btn-outline-secondary dropdown-toggle"
                              type="button"
                              data-bs-toggle="dropdown">
                        <i class="bi bi-three-dots"></i>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end">
                        <li><a class="dropdown-item" href="#" @click="viewGroup(group)">View Details</a></li>
                        <li><a class="dropdown-item" href="#" @click="editGroup(group)" v-if="(group.status === 'active' || !group.status) && canManageGroup(group)">Edit</a></li>
                        <li><a class="dropdown-item" href="#" @click="manageMembers(group)" v-if="(group.status === 'active' || !group.status) && canManageGroup(group)">Manage Members</a></li>
                        <li><hr class="dropdown-divider"></li>
                        <li><a class="dropdown-item text-danger" href="#" @click="showDeactivateConfirmation(group)" v-if="(group.status === 'active' || !group.status) && canManageGroup(group)">Deactivate</a></li>
                        <li><a class="dropdown-item text-success" href="#" @click="showActivateConfirmation(group)" v-if="group.status === 'inactive' && isSuperAdmin">Activate</a></li>
                      </ul>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <!-- Empty State -->
            <div v-if="!loading && filteredGroups.length === 0" class="text-center py-5">
              <i class="bi bi-people display-1 text-muted"></i>
              <h4 class="mt-3">No working groups found</h4>
              <p class="text-muted" v-if="searchTerm || filterStatus">
                Try adjusting your search or filter criteria
              </p>
              <p class="text-muted" v-else>
                Create your first working group to get started
              </p>
              <button class="btn btn-primary" @click="showCreateModal = true">
                <i class="bi bi-plus me-2"></i>Create Group
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div class="modal fade" :class="{ show: showConfirmationModal, 'd-block': showConfirmationModal }" v-if="showConfirmationModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title d-flex align-items-center">
              <i :class="confirmationData.icon" class="me-2" :style="{ color: confirmationData.iconColor }"></i>
              {{ confirmationData.title }}
            </h5>
            <button type="button" class="btn-close" @click="closeConfirmation"></button>
          </div>
          <div class="modal-body pt-0">
            <p class="mb-0">{{ confirmationData.message }}</p>
            <div v-if="confirmationData.details" class="mt-3 p-3 bg-light rounded">
              <strong>{{ confirmationData.details.name }}</strong>
              <br>
              <small class="text-muted">{{ confirmationData.details.subtitle }}</small>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-outline-secondary" @click="closeConfirmation">
              Cancel
            </button>
            <button
              type="button"
              :class="confirmationData.buttonClass"
              @click="confirmAction"
              :disabled="confirmationData.processing"
            >
              <span v-if="confirmationData.processing" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <i v-else :class="confirmationData.buttonIcon" class="me-2"></i>
              {{ confirmationData.processing ? 'Processing...' : confirmationData.buttonText }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Group Modal -->
    <div class="modal fade" :class="{ show: showCreateModal, 'd-block': showCreateModal }" v-if="showCreateModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingGroup ? 'Edit' : 'Create' }} Working Group</h5>
            <button type="button" class="btn-close" @click="showCreateModal = false"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveGroup">
              <div class="mb-3">
                <label class="form-label">Group Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="groupForm.name" required>
              </div>

              <!-- Admin Selection -->
              <div class="mb-3">
                <label class="form-label">
                  <i class="bi bi-person-badge me-2"></i>Group Admins
                </label>
                <div class="position-relative">
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Search for EDCTP Admin or Super Admin..."
                    v-model="adminSearchTerm"
                    @input="searchAdmins"
                    @focus="showAdminDropdown = true"
                  >
                  <!-- Selected Admins Display -->
                  <div
                    v-for="admin in selectedAdmins"
                    :key="admin.id"
                    class="mt-2 p-2 bg-light rounded d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div class="fw-semibold">{{ admin.name }}</div>
                      <small class="text-muted">{{ admin.email }}</small>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-danger" @click="removeSelectedAdmin(admin.id)">
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                  <!-- Admin Search Results -->
                  <div v-if="showAdminDropdown && adminSearchResults.length > 0" class="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style="z-index: 1000; max-height: 200px; overflow-y: auto;">
                    <div
                      v-for="admin in adminSearchResults"
                      :key="admin.id"
                      class="p-2 hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                      @click="selectAdmin(admin)"
                    >
                      <div>
                        <div class="fw-semibold">{{ admin.name }}</div>
                        <small class="text-muted">{{ admin.email }}</small>
                      </div>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-primary"
                        :disabled="selectedAdmins.some(item => item.id === admin.id)"
                      >
                        <i class="bi bi-plus"></i>
                      </button>
                    </div>
                  </div>
                  <!-- Loading indicator -->
                  <div v-if="loadingAdmins" class="position-absolute w-100 mt-1 bg-white border rounded shadow-sm p-2">
                    <div class="text-center">
                      <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      Loading admins...
                    </div>
                  </div>
                </div>
                <small class="text-muted">Only EDCTP Admin or Super Admin can be assigned as group admins</small>
              </div>

              <!-- Status field (only visible in edit mode) -->
              <div class="mb-3" v-if="editingGroup">
                <label class="form-label">Status</label>
                <div class="form-control-plaintext">
                  <span class="badge" :class="getStatusBadgeClass(groupForm.status)">
                    {{ groupForm.status === 'active' ? 'Active' : 'Inactive' }}
                  </span>
                </div>
                <small class="text-muted">Status is automatically managed by the system</small>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeCreateModal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveGroup" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ editingGroup ? 'Update' : 'Create' }} Group
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Manage Members Modal with Pending Approvals Tab -->
    <div class="modal fade" :class="{ show: showMembersModal, 'd-block': showMembersModal }" v-if="showMembersModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Manage Members - {{ selectedGroup?.name }}</h5>
            <button type="button" class="btn-close" @click="showMembersModal = false"></button>
          </div>
          <div class="modal-body">
            <!-- Tabs Navigation -->
            <ul class="nav nav-tabs mb-3" role="tablist">
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ active: activeTab === 'members' }"
                  @click="activeTab = 'members'"
                  type="button"
                >
                  <i class="bi bi-people me-2"></i>
                  Current Members ({{ sortedFilteredGroupMembers.length }})
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link position-relative"
                  :class="{ active: activeTab === 'pending' }"
                  @click="switchToPendingTab"
                  type="button"
                >
                  <i class="bi bi-person-check me-2"></i>
                  Pending Requests
                  <span v-if="sortedFilteredPendingApprovals.length > 0" class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {{ sortedFilteredPendingApprovals.length }}
                  </span>
                </button>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="tab-content">
              <!-- Current Members Tab -->
              <div v-show="activeTab === 'members'" class="tab-pane fade" :class="{ 'show active': activeTab === 'members' }">
                <h6>Current Members ({{ sortedFilteredGroupMembers.length }})</h6>
                <div class="mb-3">
                  <input
                    type="text"
                    class="form-control"
                    v-model="memberSearchTerm"
                    placeholder="Search current members by name or email..."
                  >
                </div>
                <div v-if="loadingMembers" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  Loading members...
                </div>
                <div v-else-if="sortedFilteredGroupMembers.length === 0" class="text-center py-4 text-muted">
                  <i class="bi bi-search me-2"></i>No members found for your search.
                </div>
                <div v-else class="list-group" style="max-height: 400px; overflow-y: auto;">
                  <div v-for="member in sortedFilteredGroupMembers" :key="member.id" class="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <div class="fw-semibold">{{ member.name }}</div>
                      <small class="text-muted">{{ member.email }}</small>
                      <div v-if="member.pivot && member.pivot.joined_at">
                        <small class="text-muted">Joined {{ formatDate(member.pivot.joined_at) }}</small>
                      </div>
                    </div>
                    <button class="btn btn-sm btn-outline-danger" @click="removeMember(member)" v-if="member.id !== currentUserId">
                      <i class="bi bi-person-dash"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Pending Approvals Tab -->
              <div v-show="activeTab === 'pending'" class="tab-pane fade" :class="{ 'show active': activeTab === 'pending' }">
                <h6>Pending Approval Requests</h6>
                <div class="mb-3">
                  <input
                    type="text"
                    class="form-control"
                    v-model="pendingSearchTerm"
                    placeholder="Search pending requests by name or email..."
                  >
                </div>

                <!-- Loading State -->
                <div v-if="loadingApprovals" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-2">Loading pending requests...</p>
                </div>

                <!-- No Pending Requests -->
                <div v-else-if="sortedFilteredPendingApprovals.length === 0" class="text-center py-4">
                  <i class="bi bi-check-circle text-success" style="font-size: var(--font-size-4xl);"></i>
                  <h5 class="mt-3">No Matching Pending Requests</h5>
                  <p class="text-muted">There are no pending requests matching your search.</p>
                </div>

                <!-- Pending Requests List -->
                <div v-else class="list-group" style="max-height: 400px; overflow-y: auto;">
                  <div
                    v-for="approval in sortedFilteredPendingApprovals"
                    :key="approval.id"
                    class="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div class="fw-semibold">{{ approval.user.name }}</div>
                      <small class="text-muted">{{ approval.user.email }}</small>
                      <div v-if="approval.user.title">
                        <small class="text-muted">{{ approval.user.title }}</small>
                      </div>
                      <div class="mt-1">
                        <small class="text-muted">
                          <i class="bi bi-clock me-1"></i>
                          Requested {{ formatDate(approval.created_at) }}
                        </small>
                      </div>
                      <div class="mt-1">
                        <small class="text-muted">
                          {{ approval.request_type === 'leave' ? 'Leave request' : 'Join request' }}
                        </small>
                      </div>
                    </div>
                    <div class="d-flex gap-2">
                      <button
                        class="btn btn-sm btn-success"
                        @click="approveUser(approval)"
                        :disabled="processingApproval"
                      >
                        <span v-if="processingApproval" class="spinner-border spinner-border-sm me-1" role="status"></span>
                        <i v-else class="bi bi-check-lg me-1"></i>
                        Approve
                      </button>
                      <button
                        class="btn btn-sm btn-danger"
                        @click="rejectUser(approval)"
                        :disabled="processingApproval"
                      >
                        <i class="bi bi-x-lg me-1"></i>
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showMembersModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Group Details Modal -->
    <div class="modal fade" :class="{ show: showDetailsModal, 'd-block': showDetailsModal }" v-if="showDetailsModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ selectedGroup?.name }}</h5>
            <button type="button" class="btn-close" @click="showDetailsModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="row" v-if="selectedGroup">
              <div class="col-md-8">
                <h6>Group Details</h6>
                <p><strong>Name:</strong> {{ selectedGroup.name }}</p>
                <p><strong>Status:</strong>
                  <span class="badge" :class="getStatusBadgeClass(selectedGroup.status)">
                    {{ selectedGroup.status || 'active' }}
                  </span>
                </p>

                <!-- Group Admins Section -->
                <div class="mb-3">
                  <h6 class="d-flex align-items-center justify-content-between">
                    <span><i class="bi bi-person-badge me-2"></i>Group Admins</span>
                    <button
                      v-if="isSuperAdmin || isEdctpAdmin"
                      class="btn btn-sm btn-primary"
                      @click="showChangeAdminModal = true"
                    >
                      <i class="bi bi-plus me-1"></i>Add Admin
                    </button>
                  </h6>
                  <div v-if="selectedGroup.admins && selectedGroup.admins.length > 0" class="list-group">
                    <div
                      v-for="admin in selectedGroup.admins"
                      :key="admin.id"
                      class="list-group-item d-flex align-items-center justify-content-between"
                    >
                      <div>
                        <div class="fw-semibold">{{ admin.name }}</div>
                        <small class="text-muted">{{ admin.email }}</small>
                      </div>
                      <button
                        v-if="isSuperAdmin"
                        class="btn btn-sm btn-outline-danger"
                        @click="removeGroupAdmin(admin)"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                  <div v-else class="alert alert-warning">
                    <i class="bi bi-exclamation-triangle me-2"></i>No admins assigned to this group
                  </div>
                </div>

                <p><strong>Created:</strong> {{ formatDate(selectedGroup.created_at) }}</p>
              </div>
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">Group Information</h6>
                  </div>
                  <div class="card-body">
                    <p><strong>Members:</strong> {{ selectedGroup.member_count || 0 }}</p>
                    <p><strong>Created:</strong> {{ formatDate(selectedGroup.created_at) }}</p>
                    <p><strong>Status:</strong>
                      <span class="badge" :class="getStatusBadgeClass(selectedGroup.status)">
                        {{ selectedGroup.status || 'active' }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Change Admin Modal -->
    <div class="modal fade" :class="{ show: showChangeAdminModal, 'd-block': showChangeAdminModal }" v-if="showChangeAdminModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-person-badge me-2"></i>
              {{ selectedGroup?.admins?.length ? 'Add' : 'Assign' }} Group Admin
            </h5>
            <button type="button" class="btn-close" @click="showChangeAdminModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <AdminSelector
                label="Select New Admin"
                placeholder="Search for EDCTP Admin or Super Admin..."
                :search-term="changeAdminSearchTerm"
                :search-results="changeAdminSearchResults"
                :show-dropdown="showChangeAdminDropdown"
                :loading="loadingAdmins"
                :selected-admin="newSelectedAdmin"
                :existing-admins="selectedGroup && selectedGroup.admins ? selectedGroup.admins : []"
                existing-admins-title="Current admins on this group"
                @update:search-term="handleChangeAdminSearchInput"
                @focus-input="showChangeAdminDropdown = true"
                @select-admin="selectNewAdmin"
                @clear-selected="newSelectedAdmin = null"
                @remove-admin="removeAdminFromSelectedGroup"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showChangeAdminModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="updateGroupAdmin" :disabled="!newSelectedAdmin || updatingAdmin">
              <span v-if="updatingAdmin" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ selectedGroup?.admins?.length ? 'Add' : 'Assign' }} Admin
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import workingGroupServices from "@/services/workinggroups/WorkingGroupServices";
import UserService from "@/services/users/UserService";
import AdminSelector from "@/components/common/AdminSelector.vue";

export default {
  name: 'WorkingGroups',
  components: {
    AdminSelector
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadingMembers: false,
      searchingUsers: false,
      sendingInvites: false,
      loadingAdmins: false,
      updatingAdmin: false,
      loadingApprovals: false,
      processingApproval: false,
      allUsers: [],
      adminUsers: [],
      activeView: 'list',
      activeTab: 'members',
      searchTerm: '',
      memberSearchTerm: '',
      pendingSearchTerm: '',
      filterStatus: '',
      showCreateModal: false,
      showDetailsModal: false,
      showMembersModal: false,
      showConfirmationModal: false,
      showAdminDropdown: false,
      showChangeAdminModal: false,
      showChangeAdminDropdown: false,
      editingGroup: null,
      selectedGroup: null,
      selectedAdmins: [],
      newSelectedAdmin: null,
      userSearchTerm: '',
      adminSearchTerm: '',
      changeAdminSearchTerm: '',
      userSearchResults: [],
      adminSearchResults: [],
      changeAdminSearchResults: [],
      pendingInvites: [],
      pendingApprovals: [],
      groupMembers: [],
      currentUserId: null,
      currentUserRoles: [],
      groupForm: {
        name: '',
        status: 'active',
        admin_ids: []
      },
      workingGroups: [],
      confirmationData: {
        title: '',
        message: '',
        details: null,
        icon: '',
        iconColor: '',
        buttonText: '',
        buttonIcon: '',
        buttonClass: '',
        action: null,
        processing: false
      },
      toast: {
        show: false,
        message: '',
        type: 'success'
      }
    }
  },
  computed: {
    filteredGroups() {
      let filtered = this.workingGroups
      if (this.searchTerm) {
        filtered = filtered.filter(group =>
          group.name && group.name.toLowerCase().includes(this.searchTerm.toLowerCase())
        )
      }
      if (this.filterStatus) {
        filtered = filtered.filter(group => {
          const status = group.status || 'active'
          return status === this.filterStatus
        })
      }
      return filtered
    },
    isSuperAdmin() {
      return this.currentUserRoles.includes('Super Admin')
    },
    isEdctpAdmin() {
      return this.currentUserRoles.includes('EDCTP Admin')
    },
    sortedFilteredGroupMembers() {
      const search = String(this.memberSearchTerm || '').trim().toLowerCase()
      const members = Array.isArray(this.groupMembers) ? this.groupMembers : []

      return members
        .filter((member) => {
          if (!search) return true
          const name = String(member?.name || '').toLowerCase()
          const email = String(member?.email || '').toLowerCase()
          return name.includes(search) || email.includes(search)
        })
        .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' }))
    },
    sortedFilteredPendingApprovals() {
      const search = String(this.pendingSearchTerm || '').trim().toLowerCase()
      const approvals = Array.isArray(this.pendingApprovals) ? this.pendingApprovals : []

      return approvals
        .filter((approval) => {
          if (!search) return true
          const name = String(approval?.user?.name || '').toLowerCase()
          const email = String(approval?.user?.email || '').toLowerCase()
          return name.includes(search) || email.includes(search)
        })
        .sort((a, b) => String(a?.user?.name || '').localeCompare(String(b?.user?.name || ''), undefined, { sensitivity: 'base' }))
    }
  },
  async mounted() {
    await this.loadWorkingGroups()
    await this.loadAllUsers()
    await this.loadAdminUsers()
    this.getCurrentUser()

    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(event) {
      if (!event.target.closest('.position-relative')) {
        this.showAdminDropdown = false
        this.showChangeAdminDropdown = false
      }
    },

    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    async getCurrentUser() {
      try {
        const response = await UserService.getCurrentUser()
        const user =
          response?.Data?.data?.user ||
          response?.data?.data?.user ||
          response?.data?.user ||
          response?.user ||
          null

        if (!user) {
          console.error('⚠️ No user found in API response', response)
          return null
        }

        const roles = user.roles?.map(r => r.name) || []

        this.currentUserId = user.id
        this.currentUserRoles = roles
        return user
      } catch (error) {
        console.error('❌ Error fetching current user:', error)
        if (error?.response?.status === 401) {
          console.warn('⚠️ Unauthorized, please reauthenticate.')
        }
        return null
      }
    },

    canManageGroup(group) {
      if (this.isSuperAdmin) {
        return true
      }
      if (group.admins && Array.isArray(group.admins)) {
        return group.admins.some(admin => admin.id === this.currentUserId)
      }
      return false
    },

    async loadAdminUsers() {
      this.loadingAdmins = true
      try {
        const response = await workingGroupServices.getAdminUsers()

        let raw = []

        if (response?.Data?.success) {
          raw = response.Data.data || []
        } else if (response?.data?.success) {
          raw = response.data.data || []
        } else if (Array.isArray(response)) {
          raw = response
        }

        // ✅ NORMALIZE STRUCTURE
        this.adminUsers = raw
          .map(item => {
            const user = item.user ?? item
            return {
              id: user.id,
              name: user.name,
              email: user.email
            }
          })
          .filter(u => u.id && u.email)

        console.log('✅ Loaded admin users:', this.adminUsers)

      } catch (error) {
        console.error('❌ Load admin users error:', error)
        this.adminUsers = []
      } finally {
        this.loadingAdmins = false
      }
    },

    searchAdminsForChange() {
      if (this.changeAdminSearchTerm.length < 2) {
        this.changeAdminSearchResults = []
        this.showChangeAdminDropdown = false
        return
      }

      const term = this.changeAdminSearchTerm.toLowerCase()

      this.changeAdminSearchResults = this.adminUsers.filter(admin =>
        admin.name?.toLowerCase().includes(term) ||
        admin.email?.toLowerCase().includes(term)
      )

      this.showChangeAdminDropdown = this.changeAdminSearchResults.length > 0
    },

    handleAdminSearchInput(value) {
      this.adminSearchTerm = value
      this.searchAdmins()
    },

    handleChangeAdminSearchInput(value) {
      this.changeAdminSearchTerm = value
      this.searchAdminsForChange()
    },

    selectAdmin(admin) {
      if (!this.selectedAdmins.some(item => item.id === admin.id)) {
        this.selectedAdmins.push(admin)
      }
      this.groupForm.admin_ids = this.selectedAdmins.map(item => item.id)
      this.adminSearchTerm = ''
      this.adminSearchResults = []
      this.showAdminDropdown = false
    },

    removeSelectedAdmin(adminId) {
      this.selectedAdmins = this.selectedAdmins.filter(admin => admin.id !== adminId)
      this.groupForm.admin_ids = this.selectedAdmins.map(admin => admin.id)
    },

    removeAdminFromEditingGroup(admin) {
      if (this.editingGroup && this.editingGroup.admins) {
        this.editingGroup.admins = this.editingGroup.admins.filter(a => a.id !== admin.id)
      }
    },

    removeAdminFromSelectedGroup(admin) {
      if (this.selectedGroup && this.selectedGroup.admins) {
        this.selectedGroup.admins = this.selectedGroup.admins.filter(a => a.id !== admin.id)
      }
    },

    searchAdmins() {
      if (this.adminSearchTerm.length < 2) {
        this.adminSearchResults = []
        this.showAdminDropdown = false
        return
      }

      const term = this.adminSearchTerm.toLowerCase()

      this.adminSearchResults = this.adminUsers.filter(admin => {
        const matchesSearch =
          admin.name?.toLowerCase().includes(term) ||
          admin.email?.toLowerCase().includes(term)
        const alreadySelected = this.selectedAdmins.some(item => item.id === admin.id)
        return matchesSearch && !alreadySelected
      })

      this.showAdminDropdown = this.adminSearchResults.length > 0
    },

    selectNewAdmin(admin) {
      this.newSelectedAdmin = admin

      // Add admin to existing admins list if not already present
      if (this.selectedGroup && this.selectedGroup.admins) {
        const exists = this.selectedGroup.admins.some(a => a.id === admin.id)
        if (!exists) {
          this.selectedGroup.admins.push(admin)
        }
      }

      this.changeAdminSearchTerm = ''
      this.changeAdminSearchResults = []
      this.showChangeAdminDropdown = false
    },

    async updateGroupAdmin() {
      if (!this.newSelectedAdmin || !this.selectedGroup) return

      this.updatingAdmin = true
      try {
        const response = await workingGroupServices.addWorkingGroupAdmin(this.selectedGroup.id, {
          user_id: this.newSelectedAdmin.id
        })

        let success = false
        if (response && response.Data && response.Data.success) {
          success = true
        } else if (response && response.data && response.data.success) {
          success = true
        }

        if (success) {
          await this.reloadGroup(this.selectedGroup.id)

          this.showToast('success', 'Group admin added successfully!')
          this.showChangeAdminModal = false
          this.newSelectedAdmin = null
          this.changeAdminSearchTerm = ''
        } else {
          this.showToast('error', 'Failed to add group admin')
        }
      } catch (error) {
        console.error('Add admin error:', error)
        this.showToast('error', 'Failed to add group admin')
      } finally {
        this.updatingAdmin = false
      }
    },

    async reloadGroup(groupId) {
      try {
        const response = await workingGroupServices.getWorkingGroup(groupId)
        let data = null
        if (response && response.Data && response.Data.success && response.Data.data) {
          data = response.Data.data
        } else if (response && response.data && response.data.success && response.data.data) {
          data = response.data.data
        }

        if (data) {
          const groupIndex = this.workingGroups.findIndex(g => g.id === groupId)
          if (groupIndex > -1) {
            this.workingGroups[groupIndex] = {
              ...this.workingGroups[groupIndex],
              ...data
            }
          }
          if (this.selectedGroup && this.selectedGroup.id === groupId) {
            this.selectedGroup = {
              ...this.selectedGroup,
              ...data
            }
          }
        }
      } catch (error) {
        console.error('Reload group error:', error)
      }
    },

    async refreshWorkingGroupsState(groupId = null) {
      await this.loadWorkingGroups()

      if (!groupId) {
        return
      }

      const updatedGroup = this.workingGroups.find(g => g.id === groupId)
      if (updatedGroup) {
        this.selectedGroup = {
          ...this.selectedGroup,
          ...updatedGroup
        }
      }

      if (this.showMembersModal && this.selectedGroup?.id === groupId) {
        await this.loadGroupMembers(groupId)
      }

      if (this.activeTab === 'pending' && this.selectedGroup?.id === groupId) {
        await this.loadPendingApprovals(groupId)
      }
    },

    async removeGroupAdmin(admin) {
      if (!this.selectedGroup || !admin) return

      const adminId = admin.id || admin.user_id
      if (!adminId) {
        this.showToast('error', 'Unable to remove admin: missing admin ID')
        return
      }

      try {
        const response = await workingGroupServices.removeWorkingGroupAdmin(this.selectedGroup.id, adminId)

        let success = false
        if (response && response.Data && response.Data.success) {
          success = true
        } else if (response && response.data && response.data.success) {
          success = true
        }

        if (success) {
          await this.refreshWorkingGroupsState(this.selectedGroup.id)
          this.showToast('success', 'Admin removed successfully!')
        } else {
          this.showToast('error', 'Failed to remove admin')
        }
      } catch (error) {
        console.error('Remove admin error:', error)
        this.showToast('error', 'Failed to remove admin')
      }
    },

    showDeactivateConfirmation(group) {
      this.confirmationData = {
        title: 'Deactivate Working Group',
        message: 'Are you sure you want to deactivate this working group?',
        details: {
          name: group.name,
          subtitle: `${group.member_count || 0} members will be affected`
        },
        icon: 'bi bi-exclamation-triangle-fill',
        iconColor: '#dc3545',
        buttonText: 'Deactivate',
        buttonIcon: 'bi bi-pause-circle',
        buttonClass: 'btn btn-danger',
        action: () => this.deactivateGroup(group),
        processing: false
      }
      this.showConfirmationModal = true
    },

    showActivateConfirmation(group) {
      this.confirmationData = {
        title: 'Activate Working Group',
        message: 'Are you sure you want to activate this working group?',
        details: {
          name: group.name,
          subtitle: 'Members will be able to access the group again'
        },
        icon: 'bi bi-check-circle-fill',
        iconColor: '#198754',
        buttonText: 'Activate',
        buttonIcon: 'bi bi-play-circle',
        buttonClass: 'btn btn-success',
        action: () => this.activateGroup(group),
        processing: false
      }
      this.showConfirmationModal = true
    },

    async confirmAction() {
      if (this.confirmationData.action) {
        this.confirmationData.processing = true
        try {
          await this.confirmationData.action()
          this.closeConfirmation()
        } catch (error) {
          console.error('Confirmation action error:', error)
          this.confirmationData.processing = false
        }
      }
    },

    closeConfirmation() {
      this.showConfirmationModal = false
      this.confirmationData = {
        title: '',
        message: '',
        details: null,
        icon: '',
        iconColor: '',
        buttonText: '',
        buttonIcon: '',
        buttonClass: '',
        action: null,
        processing: false
      }
    },

    async loadWorkingGroups() {
      this.loading = true
      try {
        const response = await workingGroupServices.getWorkingGroups()

        let data = null
        if (response && response.Data && response.Data.success && response.Data.data) {
          data = response.Data.data
        } else if (response && response.data && response.data.success && response.data.data) {
          data = response.data.data
        } else if (response && response.Data && Array.isArray(response.Data.data)) {
          data = response.Data.data
        } else if (response && response.data && Array.isArray(response.data)) {
          data = response.data
        } else if (Array.isArray(response)) {
          data = response
        }

        if (data && Array.isArray(data)) {
          this.workingGroups = data.map(group => ({
            ...group,
            status: group.status || (group.deleted_at ? 'inactive' : 'active'),
            member_count: group.member_count || 0
          }))
        } else {
          console.error('Unexpected data structure:', response)
          this.showToast('error', 'Unexpected data format from server')
          this.workingGroups = []
        }
      } catch (error) {
        console.error('Load working groups error:', error)
        this.showToast('error', 'Failed to load working groups: ' + (error.message || 'Unknown error'))
      } finally {
        this.loading = false
      }
    },

    async loadAllUsers() {
      try {
        const response = await UserService.getUsers({per_page: 1000})

        let usersData = []
        if (response && response.Data && response.Data.success && response.Data.data) {
          usersData = response.Data.data
        } else if (response && response.data && response.data.success && response.data.data) {
          usersData = response.data.data
        } else if (response && response.Data && Array.isArray(response.Data.data)) {
          usersData = response.Data.data
        } else if (response && response.data && Array.isArray(response.data)) {
          usersData = response.data
        } else if (Array.isArray(response)) {
          usersData = response
        }

        this.allUsers = usersData.filter(user => user && user.email)
      } catch (error) {
        console.error('Load users error:', error)
        this.showToast('error', 'Failed to load users')
        this.allUsers = []
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString()
    },

    getStatusBadgeClass(status) {
      const classes = {
        'active': 'bg-success',
        'inactive': 'bg-warning'
      }
      return classes[status || 'active'] || 'bg-success'
    },

    viewGroup(group) {
      this.selectedGroup = group
      this.showDetailsModal = true
    },

    editGroup(group) {
      this.editingGroup = group
      const admins = Array.isArray(group.admins) ? group.admins : []
      this.groupForm = {
        name: group.name,
        status: group.status || 'active',
        admin_ids: admins.map(admin => admin.id)
      }
      this.selectedAdmins = [...admins]
      this.showCreateModal = true
    },

    async manageMembers(group) {
      this.selectedGroup = group
      this.activeTab = 'members'
      await this.loadGroupMembers(group.id)
      await this.loadPendingApprovals(group.id)
      this.memberSearchTerm = ''
      this.pendingSearchTerm = ''
      this.userSearchTerm = ''
      this.userSearchResults = []
      this.pendingInvites = []
      this.showMembersModal = true
    },

    async switchToPendingTab() {
      this.activeTab = 'pending'
      if (this.selectedGroup) {
        await this.loadPendingApprovals(this.selectedGroup.id)
      }
    },

    async loadPendingApprovals(groupId) {
      this.loadingApprovals = true
      try {
        const response = await workingGroupServices.getPendingApprovals(groupId)

        if (response && response.Data && response.Data.success) {
          this.pendingApprovals = response.Data.data || []
        } else if (response && response.data && response.data.success) {
          this.pendingApprovals = response.data.data || []
        } else {
          this.pendingApprovals = []
        }
      } catch (error) {
        console.error('Load pending approvals error:', error)
        this.showToast('error', 'Failed to load pending approval requests')
        this.pendingApprovals = []
      } finally {
        this.loadingApprovals = false
      }
    },

    async approveUser(approval) {
      this.processingApproval = true
      try {
        const response = await workingGroupServices.approveRequest(
          this.selectedGroup.id,
          approval.id
        )

        if (response && ((response.Data && response.Data.success) || (response.data && response.data.success))) {
          this.showToast('success', 'User approved successfully!')
          await this.refreshWorkingGroupsState(this.selectedGroup.id)
        } else {
          this.showToast('error', 'Failed to approve user')
        }
      } catch (error) {
        console.error('Approve user error:', error)
        this.showToast('error', 'Failed to approve user')
      } finally {
        this.processingApproval = false
      }
    },

    async rejectUser(approval) {
      this.processingApproval = true
      try {
        const response = await workingGroupServices.rejectRequest(
          this.selectedGroup.id,
          approval.id
        )

        if (response && ((response.Data && response.Data.success) || (response.data && response.data.success))) {
          this.showToast('success', 'User request rejected')
          await this.refreshWorkingGroupsState(this.selectedGroup.id)
        } else {
          this.showToast('error', 'Failed to reject user request')
        }
      } catch (error) {
        console.error('Reject user error:', error)
        this.showToast('error', 'Failed to reject user request')
      } finally {
        this.processingApproval = false
      }
    },

    async loadGroupMembers(groupId) {
      this.loadingMembers = true
      try {
        const response = await workingGroupServices.getWorkingGroupMembers(groupId)

        let members = []
        if (response && response.Data && response.Data.success && response.Data.data) {
          members = response.Data.data
        } else if (response && response.data && response.data.success && response.data.data) {
          members = response.data.data
        } else if (response && response.Data && Array.isArray(response.Data.data)) {
          members = response.Data.data
        } else if (response && response.data && Array.isArray(response.data)) {
          members = response.data
        } else if (Array.isArray(response)) {
          members = response
        }

        this.groupMembers = members || []
      } catch (error) {
        console.error('Load group members error:', error)
        this.showToast('error', 'Failed to load group members')
        this.groupMembers = []
      } finally {
        this.loadingMembers = false
      }
    },

    async searchUsers() {
      if (this.userSearchTerm.length < 2) {
        this.userSearchResults = []
        return
      }
      this.searchingUsers = true
      try {
        const response = await workingGroupServices.searchUsers(
          this.userSearchTerm,
          this.selectedGroup?.id
        )

        let users = []

        if (response && response.Data && response.Data.success && response.Data.data) {
          users = response.Data.data
        } else if (response && response.data && response.data.success && response.data.data) {
          users = response.data.data
        } else if (response && response.Data && Array.isArray(response.Data.data)) {
          users = response.Data.data
        } else if (response && response.data && Array.isArray(response.data)) {
          users = response.data
        } else if (Array.isArray(response)) {
          users = response
        }

        if (users && users.length > 0) {
          const pendingIds = this.pendingInvites.map(p => p.id)
          const memberIds = this.groupMembers.map(m => m.id)
          const excludeIds = [...memberIds, ...pendingIds]

          this.userSearchResults = users.filter(user =>
            !excludeIds.includes(user.id)
          )
        } else {
          const memberIds = this.groupMembers.map(m => m.id)
          const pendingIds = this.pendingInvites.map(p => p.id)
          const excludeIds = [...memberIds, ...pendingIds]

          this.userSearchResults = this.allUsers.filter(user =>
            !excludeIds.includes(user.id) &&
            (user.name.toLowerCase().includes(this.userSearchTerm.toLowerCase()) ||
              user.email.toLowerCase().includes(this.userSearchTerm.toLowerCase()))
          ).slice(0, 10)
        }
      } catch (error) {
        console.error('Search users error:', error)
        const memberIds = this.groupMembers.map(m => m.id)
        const pendingIds = this.pendingInvites.map(p => p.id)
        const excludeIds = [...memberIds, ...pendingIds]

        this.userSearchResults = this.allUsers.filter(user =>
          !excludeIds.includes(user.id) &&
          (user.name.toLowerCase().includes(this.userSearchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(this.userSearchTerm.toLowerCase()))
        ).slice(0, 10)
      } finally {
        this.searchingUsers = false
      }
    },

    inviteUser(user) {
      if (!this.pendingInvites.find(u => u.id === user.id)) {
        this.pendingInvites.push(user)
      }
      this.userSearchTerm = ''
      this.userSearchResults = []
    },

    removePendingInvite(user) {
      const index = this.pendingInvites.findIndex(u => u.id === user.id)
      if (index > -1) {
        this.pendingInvites.splice(index, 1)
      }
    },

    async sendInvitations() {
      if (this.pendingInvites.length === 0) return
      this.sendingInvites = true
      try {
        const userIds = this.pendingInvites.map(user => user.id)
        const response = await workingGroupServices.addWorkingGroupMember(this.selectedGroup.id, {
          user_ids: userIds
        })
        let success = false
        if (response && response.Data && response.Data.success) {
          success = true
        } else if (response && response.data && response.data.success) {
          success = true
        } else if (response && response.Success) {
          success = true
        } else if (response && response.Data && response.Data.data) {
          success = true
        }

        if (success) {
          await this.refreshWorkingGroupsState(this.selectedGroup.id)
          this.pendingInvites = []
          this.showToast('success', 'Invitations sent successfully!')
        } else {
          console.error('Invitation failed - no success indicator in response')
          this.showToast('error', 'Failed to send invitations - server response was unexpected')
        }
      } catch (error) {
        console.error('Send invitations error:', error)
        this.showToast('error', 'Failed to send invitations')
      } finally {
        this.sendingInvites = false
      }
    },

    async removeMember(member) {
      try {
        const response = await workingGroupServices.removeMember(this.selectedGroup.id, member.id)
        let success = false
        if (response && response.Data && response.Data.success) {
          success = true
        } else if (response && response.data && response.data.success) {
          success = true
        } else if (response && response.Success) {
          success = true
        } else if (response && response.Data && response.Data.message) {
          success = true
        }

        if (success) {
          await this.refreshWorkingGroupsState(this.selectedGroup.id)
          this.showToast('success', `${member.name} has been removed from the group`)
        } else {
          console.error('Remove member failed - no success indicator')
          this.showToast('error', 'Failed to remove member - server response was unexpected')
        }
      } catch (error) {
        console.error('Remove member error:', error)
        this.showToast('error', 'Failed to remove member')
      }
    },

    async deactivateGroup(group) {
      try {
        const response = await workingGroupServices.deactivateWorkingGroup(group.id)

        let success = false
        if (response && response.Data && response.Data.success) {
          success = true
        } else if (response && response.data && response.data.success) {
          success = true
        } else if (response && response.Success) {
          success = true
        } else if (response && response.Data && response.Data.message) {
          success = true
        }

        if (success) {
          await this.refreshWorkingGroupsState(group.id)
          this.showToast('success', 'Working group has been deactivated')
        } else {
          console.error('Deactivate failed - no success indicator')
          this.showToast('error', 'Failed to deactivate working group - server response was unexpected')
        }
      } catch (error) {
        console.error('Deactivate group error:', error)
        this.showToast('error', 'Failed to deactivate working group')
      }
    },

    async activateGroup(group) {
      try {
        const response = await workingGroupServices.activateWorkingGroup(group.id)

        let success = false
        if (response && response.Data && response.Data.success) {
          success = true
        } else if (response && response.data && response.data.success) {
          success = true
        } else if (response && response.Success) {
          success = true
        } else if (response && response.Data && response.Data.message) {
          success = true
        }

        if (success) {
          await this.refreshWorkingGroupsState(group.id)
          this.showToast('success', 'Working group has been activated')
        } else {
          console.error('Activate failed - no success indicator')
          this.showToast('error', 'Failed to activate working group - server response was unexpected')
        }
      } catch (error) {
        console.error('Activate group error:', error)
        this.showToast('error', 'Failed to activate working group')
      }
    },

    async saveGroup() {
      if (!this.groupForm.name.trim()) {
        this.showToast('error', 'Please enter a group name')
        return
      }

      const existingGroup = this.workingGroups.find(
        g => g.name.toLowerCase().trim() === this.groupForm.name.toLowerCase().trim()
          && (!this.editingGroup || g.id !== this.editingGroup.id)
      )

      if (existingGroup) {
        this.showToast('error', 'A working group with this name already exists')
        return
      }

      this.saving = true
      try {
        if (this.editingGroup) {
          const response = await workingGroupServices.updateWorkingGroup(this.editingGroup.id, {
            name: this.groupForm.name,
            admin_ids: this.groupForm.admin_ids
          })

          let success = false
          if (response && response.Data && response.Data.success) {
            success = true
          } else if (response && response.data && response.data.success) {
            success = true
          } else if (response && response.Success) {
            success = true
          } else if (response && response.Data && response.Data.data) {
            success = true
          }

          if (success) {
            await this.refreshWorkingGroupsState(this.editingGroup.id)
            this.showToast('success', 'Working group updated successfully!')
            this.closeCreateModal()
          } else {
            console.error('Update failed - no success indicator')
            this.showToast('error', 'Failed to update working group')
          }
        } else {
          const response = await workingGroupServices.createWorkingGroup({
            name: this.groupForm.name,
            admin_ids: this.groupForm.admin_ids
          })

          let success = false
          let data = null

          if (response && response.Data && response.Data.success) {
            success = true
            data = response.Data.data
          } else if (response && response.data && response.data.success) {
            success = true
            data = response.data.data
          } else if (response && response.Success) {
            success = true
            data = response.Data || response.data
          } else if (response && response.Data && response.Data.data) {
            success = true
            data = response.Data.data
          }

          if (success && data) {
            await this.refreshWorkingGroupsState(data.id)
            this.showToast('success', 'Working group created successfully!')
            this.closeCreateModal()
          } else {
            console.error('Create failed', response)
            this.showToast('error', 'Failed to create working group')
          }
        }
      } catch (error) {
        console.error('Save group error:', error)
        if (error.response && error.response.status === 422) {
          const errorData = error.response.data
          if (errorData && errorData.message) {
            this.showToast('error', errorData.message)
          } else if (errorData && errorData.errors) {
            const firstError = Object.values(errorData.errors)[0]
            this.showToast('error', Array.isArray(firstError) ? firstError[0] : firstError)
          } else {
            this.showToast('error', 'Validation error')
          }
        } else {
          this.showToast('error', 'Failed to save working group')
        }
      } finally {
        this.saving = false
      }
    },

    closeCreateModal() {
      this.showCreateModal = false
      this.groupForm = {
        name: '',
        status: 'active',
        admin_ids: []
      }
      this.selectedAdmins = []
      this.adminSearchTerm = ''
      this.adminSearchResults = []
      this.showAdminDropdown = false
      this.editingGroup = null
    }
  }
}
</script>

<style scoped>
.working-groups-page {
  padding: 1rem 0;
}

.group-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: visible;
}

.group-card:hover,
.group-card:focus-within {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 20;
}

.group-card .dropdown {
  position: relative;
}

.group-card .dropdown-menu {
  z-index: 1080;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

.btn-outline-secondary.active {
  background-color: #6c757d;
  color: white;
}

.btn-outline-primary.active {
  background-color: #009CAD;
  color: white;
}

.hover-bg-light:hover {
  background-color: #f8f9fa;
}

.cursor-pointer {
  cursor: pointer;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.modal-dialog-centered {
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
}

.modal-content {
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.toast {
  min-width: 300px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  border: none;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.toast.show {
  opacity: 1;
}

.toast-header {
  border-bottom: none;
  padding: 0.75rem 1rem;
}

.toast-body {
  padding: 0.75rem 1rem;
  font-weight: var(--font-weight-medium);
}

.bg-success .toast-header,
.bg-success .toast-body {
  background-color: #198754 !important;
}

.bg-danger .toast-header,
.bg-danger .toast-body {
  background-color: #dc3545 !important;
}

.nav-tabs .nav-link {
  color: #495057;
  border: 1px solid transparent;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
}

.nav-tabs .nav-link.active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}
</style>
